import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { firstValueFrom, fromEvent, Subject } from 'rxjs';
import {
  filter,
  map,
  shareReplay,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';
import { ToasterService } from '@digistore/ds24-ui/toaster';
import { OrderformFascade } from '../../../../fascades/orderform.fascade';
import { OrderformOrderState } from '@orderform/widgets';

interface HeidelplayConfig {
  iframeUrl: string;
  iframeBaseUrl: string;
  debug: boolean;
  voidValue: string;
  countryMap: { [country: string]: string };
}

@Component({
  selector: 'orderform-payment-method-heidelpay-cc',
  templateUrl: './heidelpay-cc.component.html',
  styleUrls: ['./heidelpay-cc.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HeidelpayCcComponent),
      multi: true,
    },
  ],
})
export class HeidelpayCcComponent implements OnDestroy, ControlValueAccessor {
  public iframeUrl: string;
  public isDebug = false;
  public showError$ = new Subject<boolean>();

  @ViewChild('iframe', { static: true })
  iframeElement: ElementRef<HTMLIFrameElement>;

  @Input() set config(c: HeidelplayConfig) {
    this.iframeUrl = c.iframeUrl;
    this.isDebug = c.debug;
    this._config = c;
  }
  get config(): HeidelplayConfig {
    return this._config;
  }

  private _destroy$ = new Subject<void>();
  private onChange: any;
  private _config: HeidelplayConfig;
  private _beforeSubmitSubscription =
    this._orderformFascade.beforeSubmit.subscribe(() => this.beforeSubmit());
  private _windowMessage$ = fromEvent<MessageEvent>(window, 'message').pipe(
    filter((event) => event.origin === this.config.iframeBaseUrl),
    shareReplay(1)
  );

  constructor(
    private _toaster: ToasterService,
    private _orderformFascade: OrderformFascade
  ) {}

  ngOnDestroy() {
    this._destroy$.next();
    this._beforeSubmitSubscription.unsubscribe();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
    this.onChange({
      valid: true,
    });
  }

  registerOnTouched(fn: any): void {}

  writeValue(obj: any): void {}

  mapBuyerAddress(orderData: OrderformOrderState): { [key: string]: string } {
    const data = {};

    data['contact.email'] =
      orderData.order.shipping_address['buyer_address_email'] || ''; // ?? DS24_HEIDELPAY_CC_DEF_EMAIL
    data['name.given'] =
      orderData.order.shipping_address['buyer_address_first_name'] || ''; // ?? DS24_HEIDELPAY_CC_DEF_FIRST_NAME
    data['name.family'] =
      orderData.order.shipping_address['buyer_address_last_name'] || ''; // ?? DS24_HEIDELPAY_CC_DEF_LAST_NAME

    if (
      orderData.order.shipping_address['buyer_address_street'] &&
      orderData.order.shipping_address['buyer_address_street_number']
    ) {
      data['address.street'] =
        orderData.order.shipping_address['buyer_address_street'] +
        ' ' +
        orderData.order.shipping_address['buyer_address_street_number'];
    } else if (!!orderData.order.shipping_address['buyer_address_street']) {
      data['address.street'] =
        orderData.order.shipping_address['buyer_address_street'];
    } else {
      data['address.street'] = this.config.voidValue;
    }
    data['address.zip'] =
      orderData.order.shipping_address['buyer_address_zipcode'] || '';
    data['address.city'] =
      orderData.order.shipping_address['buyer_address_city'] || '';
    data['address.country'] =
      orderData.order.shipping_address['buyer_address_country'] || ''; // ?? DS24_HEIDELPAY_CC_DEF_COUNTRY
    data['address.state'] =
      orderData.order.shipping_address['buyer_address_state'] || '';

    if (
      typeof this.config.countryMap[data['address.country']] !== 'undefined'
    ) {
      data['address.country'] = this.config.countryMap[data['address.country']];
    }
    return data;
  }

  mapIframeMessage(event: MessageEvent): { valid: boolean } {
    const data = JSON.parse(event.data);

    console.log('Heidelplay Iframe Message', data);

    const is_post_data_error = data['POST.VALIDATION'] === 'NOK';
    if (is_post_data_error) {
      this.showError$.next(true);

      this.onChange({
        valid: true,
      });
      return { valid: false };
    }

    const is_processing_ok =
      data['PROCESSING.CODE'] === 'CC.RG.90.00' &&
      data['PROCESSING.RESULT'] === 'ACK';

    const is_ok = is_processing_ok && !is_post_data_error;

    const result_message = data['PROCESSING.RETURN'];
    const result_status = data['PROCESSING.STATUS'];

    if (!is_ok) {
      const is_rejected_validation =
        data['PROCESSING.STATUS'] === 'REJECTED_VALIDATION';

      let msg = is_rejected_validation
        ? $localize`:@@payment_method.error.creditcard_rejected:Die Zahlung wurde abgelehnt. Bitte probieren Sie eine andere Kreditkarte oder Bezahlweise.`
        : result_message
        ? result_message
        : $localize`:@@payment_method.error.generic:Die Zahlung konnte jetzt nicht verarbeitet werden. Bitte probieren es mit einer anderen Bezahlweise nochmal.`;

      if (result_status) {
        msg += ' (status: ' + result_status + ')';
      }

      // ajax_wait_stop();

      this._toaster.error(msg);
      return { valid: false };
    }

    const extra_params = {};

    for (const k in data) {
      if (data[k]) {
        extra_params['heidelpay_cc_' + k] = data[k];
      }
    }

    this.onChange({
      ...extra_params,
      valid: true,
    });

    return { valid: true };
  }

  beforeSubmit(): Promise<{ valid: boolean }> {
    this.showError$.next(false);
    return firstValueFrom(
      this._orderformFascade.order$.pipe(
        // Buyer Adresse auslesen und für Heidelpay aufbereiten
        take(1),
        map((orderData) => this.mapBuyerAddress(orderData)),
        switchMap((heidelpayAddressData) => {
          const addressJson = JSON.stringify(heidelpayAddressData);
          this.iframeElement.nativeElement.contentWindow.postMessage(
            addressJson,
            this.config.iframeBaseUrl
          );
          return fromEvent<MessageEvent>(window, 'message').pipe(
            takeUntil(this._destroy$),
            filter((event) => event.origin === this.config.iframeBaseUrl),
            take(1),
            map((event) => this.mapIframeMessage(event))
          );
        })
      )
    );
  }
}
